import {
  Circle,
  RectangleVertical,
  Square,
  Droplet,
  LucideIcon,
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * 水晶选项接口 - 类似FontOption的结构
 */
export interface CrystalOption {
  /** 唯一标识符 */
  id: string;
  /** 显示名称 */
  name: string;
  /** 描述信息 */
  description: string;
  /** 显示图标 */
  icon: LucideIcon;
  /** 类别 */
  category: string;
  /** 模型文件路径 */
  modelPath: string;
}

/**
 * 水晶选项配置 - 类似FONT_OPTIONS的结构
 */
export const CRYSTAL_OPTIONS: CrystalOption[] = [
  {
    id: "crystal_1",
    name: "Crystal 1",
    description: "Classic Round",
    icon: Circle,
    category: "Classic",
    modelPath: "/assets/models/crystal/crystal_1.glb",
  },
  {
    id: "crystal_2",
    name: "Crystal 2",
    description: "Vertical Rectangle",
    icon: RectangleVertical,
    category: "Modern",
    modelPath: "/assets/models/crystal/crystal_2.glb",
  },
  {
    id: "crystal_3",
    name: "Crystal 3",
    description: "Square Block",
    icon: Square,
    category: "Classic",
    modelPath: "/assets/models/crystal/crystal_3.glb",
  },
  {
    id: "crystal_4",
    name: "Crystal 4",
    description: "Droplet Shape",
    icon: Droplet,
    category: "Artistic",
    modelPath: "/assets/models/crystal/crystal_4.glb",
  },
];

/**
 * 水晶类型 - 从CRYSTAL_OPTIONS中提取的联合类型
 */
export type CrystalType = (typeof CRYSTAL_OPTIONS)[number]["id"];

/**
 * 水晶配置辅助函数
 */
export const CrystalUtils = {
  /** 获取水晶选项 */
  getOption: (id: CrystalType): CrystalOption | undefined =>
    CRYSTAL_OPTIONS.find((option) => option.id === id),

  /** 获取所有水晶ID */
  getAllIds: (): CrystalType[] => CRYSTAL_OPTIONS.map((option) => option.id),

  /** 验证水晶ID是否有效 */
  isValidId: (id: string): id is CrystalType =>
    CRYSTAL_OPTIONS.some((option) => option.id === id),
};

/**
 * 默认水晶类型
 */
export const DEFAULT_CRYSTAL_TYPE: CrystalType = "crystal_1";

interface SimpleCrystalSelectorProps {
  selectedCrystal: CrystalType;
  onCrystalSelect: (crystal: CrystalType) => void;
  className?: string;
}

/**
 * 简化的水晶选择器组件
 * 使用 shadcn RadioGroup 以列表形式展示，保持图标和文字
 */
export function SimpleCrystalSelector({
  selectedCrystal,
  onCrystalSelect,
  className,
}: SimpleCrystalSelectorProps) {
  return (
    <RadioGroup
      value={selectedCrystal}
      onValueChange={onCrystalSelect}
      className={cn("gap-2", className)}
    >
      {CRYSTAL_OPTIONS.map((crystal) => (
        <Label
          key={crystal.id}
          htmlFor={crystal.id}
          className={cn(
            "flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors hover:bg-gray-50",
            selectedCrystal === crystal.id && "bg-gray-100",
          )}
        >
          <div className="flex items-center space-x-2">
            <crystal.icon className="h-4 w-4 text-gray-600" />
            <div>
              <div className="text-xs font-medium text-gray-900">
                {crystal.name}
              </div>
              <div className="text-[10px] text-gray-500 leading-tight">
                {crystal.description}
              </div>
            </div>
          </div>
          <RadioGroupItem
            value={crystal.id}
            id={crystal.id}
            className="shrink-0"
          />
        </Label>
      ))}
    </RadioGroup>
  );
}
